import {useDebouncedValue} from '@mantine/hooks';
import {useCallback, useEffect, useRef, useState} from 'react';

import {
  ChemicalFormatType,
  EMPTY_MOLFILE,
  UNRECOGNIZED_FRAGMENTS_ERROR_CODE,
} from 'chemistry/ChemistryConstants';

import * as MoleculeDAO from './MoleculeDAO';
import type {ParseCounterpartsResponse} from './MoleculeDAO';

const DEBOUNCE_MS = 500;

export interface UseCounterpartDetectionResult {
  hasCounterparts: boolean;
  isParsing: boolean;
  isLoading: boolean;
  parseResponse: ParseCounterpartsResponse | null;
  error: string | null;
  errorCode: string | null;
  hasBlockingError: boolean;
}

/**
 * Debounces the sketcher value and calls parseCounterparts to determine whether
 * the drawn structure contains salt/solvent counterparts. Returns the full parse
 * response so consumers can render counterpart UI without making a duplicate API call.
 */
export default function useCounterpartDetection(
  sketcherValue: string | null,
  schemaId: string | undefined,
  enabled: boolean
): UseCounterpartDetectionResult {
  const [hasCounterparts, setHasCounterparts] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [parseResponse, setParseResponse] = useState<ParseCounterpartsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [hasBlockingError, setHasBlockingError] = useState(false);
  const [debouncedValue] = useDebouncedValue(sketcherValue, DEBOUNCE_MS);
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    ++requestIdRef.current;
    setHasCounterparts(false);
    setIsParsing(false);
    setIsLoading(false);
    setParseResponse(null);
    setError(null);
    setErrorCode(null);
    setHasBlockingError(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      reset();
      return;
    }
    ++requestIdRef.current;
    if (sketcherValue != null && sketcherValue !== EMPTY_MOLFILE) {
      setIsParsing(true);
      setHasCounterparts(false);
    }
  }, [sketcherValue, enabled, reset]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (debouncedValue == null || debouncedValue === EMPTY_MOLFILE) {
      reset();
      return;
    }

    const idRef = requestIdRef;
    const currentRequestId = ++idRef.current;
    setIsParsing(true);
    setIsLoading(true);
    setError(null);
    setErrorCode(null);

    MoleculeDAO.parseCounterparts(ChemicalFormatType.MOLFILE, debouncedValue, schemaId).then(
      (response) => {
        if (currentRequestId !== idRef.current) {
          return;
        }
        setParseResponse(response);
        setHasCounterparts((response.counterparts ?? []).length > 0);
        setIsLoading(false);
        setIsParsing(false);
        setHasBlockingError(false);
      },
      (err) => {
        if (currentRequestId !== idRef.current) {
          return;
        }
        const errorMessage = err?.responseJSON?.error ?? 'Failed to parse counterparts.';
        const code = err?.responseJSON?.errorCode ?? null;
        setError(errorMessage);
        setErrorCode(code);
        setParseResponse(null);
        setHasCounterparts(false);
        setIsLoading(false);
        setIsParsing(false);
        setHasBlockingError(code === UNRECOGNIZED_FRAGMENTS_ERROR_CODE);
      }
    );

    return () => {
      ++idRef.current;
    };
  }, [debouncedValue, schemaId, enabled, reset]);

  return {hasCounterparts, isParsing, isLoading, parseResponse, error, errorCode, hasBlockingError};
}
